import { call } from '@advanza/api'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FilterValue } from 'components/reports/common/ReportFilter'

// Types
export interface Filters {
    status?: string
    start_date?: string
    end_date?: string
    service_id?: string | string[] | number[]
    provider_status?: string | number
    [key: string]: FilterValue
}

export interface Pagination {
    page: number
    limit: number
    total: number
    pages: number
}

export interface ChurnReportData {
    provider_id: number
    provider_status: number
    subscription_status: string
    subscription_start_date: string | null
    subscription_end_date: string | null
    churn_date: string | null
    churn_reason: string | null
    country_code: string
    branch: string | null
    service_id: number
    first_invoice_subscription_month: string | null
    prospect_value: number
    invoice_amount_third_month: number | null
    invoice_amount_second_month: number | null
    invoice_amount_previous_month: number | null
    subscription_price: number | null
    discount_total: number | null
}

export interface Sorting {
    field: string | null
    direction: 'ASC' | 'DESC' | null
}

export interface SubscriptionChurnReportState {
    data: ChurnReportData[]
    pages: Record<number, string[]>
    rows: Record<string, any>
    pagination: Pagination | null
    filters: Filters
    sorting: Sorting
    isLoading: boolean
    error: string | null
}

// API Response Types
interface ApiResponse {
    items: ChurnReportData[]
    count: number
}

// Async Thunks
export const fetchSubscriptionChurnReport = createAsyncThunk(
    'subscriptionChurnReport/fetchData',
    async (
        params: {
            filters: Filters
            page?: number
            limit?: number
            sorting?: Sorting
            country?: string
        },
        { rejectWithValue },
    ) => {
        try {
            const { filters, page = 0, limit = 50, sorting, country } = params

            const queryParams = {
                ...filters,
                page,
                limit,
                ...(sorting?.field && { sort_field: sorting.field }),
                ...(sorting?.direction && { sort_direction: sorting.direction }),
                ...(country && { country }),
            }

            const response: ApiResponse = await call('office/presence-subscription-churn-report', {
                method: 'GET',
                query: queryParams,
            })

            // Create pages and rows structure for TableList
            const pages: Record<number, string[]> = {}
            const rows: Record<string, ChurnReportData> = {}

            // The backend already returns paginated results, so we just need to create the current page
            const currentPage = page
            const pageItems = response.items

            // Create the current page
            pages[currentPage] = pageItems.map((item, index) => `${item.provider_id}_${index}`)

            // Create rows for current page
            pageItems.forEach((item, index) => {
                const id = `${item.provider_id}_${index}`
                rows[id] = item
            })

            const totalPages = Math.ceil(response.count / limit)

            const pagination: Pagination = {
                page,
                limit,
                total: response.count,
                pages: totalPages,
            }

            return {
                data: response.items,
                pages,
                rows,
                pagination,
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Failed to fetch subscription churn report'
            return rejectWithValue(errorMessage)
        }
    },
)

const initialState: SubscriptionChurnReportState = {
    data: [],
    pages: {},
    rows: {},
    pagination: null,
    filters: {},
    sorting: {
        field: null,
        direction: null,
    },
    isLoading: false,
    error: null,
}

const subscriptionChurnReportSlice = createSlice({
    name: 'subscriptionChurnReport',
    initialState,
    reducers: {
        requestStarted: (state) => {
            state.isLoading = true
            state.error = null
        },
        requestSuccess: (
            state,
            action: PayloadAction<{
                data: ChurnReportData[]
                pages: Record<number, string[]>
                rows: Record<string, any>
                pagination: Pagination | null
            }>,
        ) => {
            state.isLoading = false
            state.data = action.payload.data
            state.pages = action.payload.pages
            state.rows = action.payload.rows
            state.pagination = action.payload.pagination
            state.error = null
        },
        requestFailed: (state, action: PayloadAction<string>) => {
            state.isLoading = false
            state.error = action.payload
        },
        filtersChanged: (state, action: PayloadAction<Filters>) => {
            state.filters = action.payload
        },
        pageChanged: (state, action: PayloadAction<number>) => {
            if (state.pagination) {
                state.pagination.page = action.payload
            }
        },
        changeFilter: (state, action: PayloadAction<{ key: string; value: FilterValue }>) => {
            state.filters[action.payload.key as keyof Filters] = action.payload
                .value as Filters[keyof Filters]
        },
        clearFilters: (state) => {
            state.filters = {}
        },
        setSorting: (
            state,
            action: PayloadAction<{ field: string; direction: 'ASC' | 'DESC' }>,
        ) => {
            state.sorting = {
                field: action.payload.field,
                direction: action.payload.direction,
            }
        },
        clearSorting: (state) => {
            state.sorting = {
                field: null,
                direction: null,
            }
        },
        resetState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSubscriptionChurnReport.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchSubscriptionChurnReport.fulfilled, (state, action) => {
                state.isLoading = false
                state.data = action.payload.data
                state.pages = action.payload.pages
                state.rows = action.payload.rows
                state.pagination = action.payload.pagination
                state.error = null
            })
            .addCase(fetchSubscriptionChurnReport.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })
    },
})

export const {
    requestStarted,
    requestSuccess,
    requestFailed,
    filtersChanged,
    pageChanged,
    changeFilter,
    clearFilters,
    setSorting,
    clearSorting,
    resetState,
} = subscriptionChurnReportSlice.actions

export default subscriptionChurnReportSlice.reducer
